/**
 * Model class for project entry validation
 */

import { required } from 'vuelidate/lib/validators'
import * as _ from 'lodash'
import Api from '@/providers/Api'
import Agency from '@/models/Agency'

const statusOptions = {
    IN_PROGRESS: 'In Progress',
    ENTERED_BUT_NOT_ATTESTED: 'Entered but not Attested',
    DRAFT: 'Draft',
    CLOSED: 'Closed',
    PAUSED: 'Paused'
}

export default {
    // helper method to populate Project options for other models
    statusOptions: statusOptions,
    async loadProjectOptions (modelField) {
        const filters = {}
        const expands = []
        const projects = Api.getItems(null, 'project', filters, {}, expands)
        modelField.options = _.map(projects, (project) => {
            return {
                value: project.id, text: project.name
            }
        })
    },
    getStatusLabel (value) {
        return statusOptions[value]
    },
    fields: {
        name: {
            label: 'Name',
            type: 'text',
            validations: { required }
        },
        notes: {
            label: 'Notes',
            type: 'textarea'
        },
        agency_id: {
            label: 'Agency',
            type: 'select',
            options: [],
            async loadOptions (model) {
                Agency.loadAgencyOptions(this)
            },
            onChange (model) {
                model.projectManagerInspector = null
                model.projectOwnerRepresentative = null
            },
            validations: { required }
        },
        department: {
            label: 'Bureau / Department',
            type: 'text'
        },
        project_number: {
            label: 'Project ID',
            type: 'text',
            helpTag: 'project-number'
        },
        proceed_date: {
            label: 'Start Date',
            type: 'date',
            helpTag: 'project-proceed-date',
            validations: { required }
        },
        end_date: {
            label: 'Estimated End Date',
            type: 'date',
            helpTag: 'project-end-date'
        },
        dollar_value: {
            label: 'Dollar Value',
            type: 'text',
            helpTag: 'project-dollar-value',
            validations: { required }
        },
        manager_type: {
            label: 'Manager Type',
            type: 'radios',
            helpTag: 'project-project-manager-type',
            onChange (model, $vform) {
                $vform.projectManagerInspector.$model = null
                $vform.projectOwnerRepresentative.$model = null
            },
            options: [
                { text: 'Project Manager / Inspector', value: 'PROJECT_MANAGER' },
                { text: 'Owner / Representative', value: 'OWNER' },
            ]
        },
        projectManagerInspector: {
            dbFieldName: 'projectManager',
            label: 'Project Manager / Inspector',
            helpTag: 'project-project-manager-inspector',
            type: 'multiselect',
            disableIf (model) {
                return !model.agency_id
            },
            showIf (model) {
                return model.manager_type === 'PROJECT_MANAGER'
            },
            multiselectOptions: {
                placeholder: 'Type project manager name to search',
                label: 'full_name_and_email',
                multiple: true,
            },
            async doSearch (query, modelInstance) {
                const filters = {
                    full_name: { field: 'full_name', type: 'hidden', value: query },
                    is_project_manger_for_agency: {
                        field: 'is_project_manager_for_agency',
                        type: 'hidden',
                        value: modelInstance.agency_id
                    },
                }
                return Api.getItems(this, 'user', filters, ['full_name_and_email'])
            },
        },
        projectOwnerRepresentative: {
            dbFieldName: 'projectManager',
            label: 'Owner / Representative',
            type: 'multiselect',
            disableIf (model) {
                return !model.agency_id
            },
            showIf (model) {
                return model.manager_type === 'OWNER'
            },
            multiselectOptions: {
                placeholder: 'Type owners representative name to search',
                label: 'full_name_and_email',
                multiple: true,
            },
            async doSearch (query, modelInstance) {
                const filters = {
                    full_name: { field: 'full_name', type: 'hidden', value: query },
                    is_owners_representative_for_agency: {
                        field: 'is_owners_representative_for_agency',
                        type: 'hidden',
                        value: modelInstance.agency_id
                    },
                }
                return Api.getItems(this, 'user', filters, ['full_name_and_email'])
            },
        },
        location: {
            label: 'Location',
            getDescription () {
                return 'Explicit help here'
            },
            type: 'map',
            helpTag: 'project-location',
            validations: { required }
        },
        primeCompanies: {
            label: 'Prime Contractors',
            type: 'multiselect',
            helpTag: 'project-prime-companies',
            multiselectOptions: {
                placeholder: 'Type company name to search',
                label: 'name',
                multiple: true,
            },
            async doSearch (query) {
                return Api.getItems(this, 'company', { name: { field: 'name', value: query } })
            }
        },
        subCompanies: {
            label: 'Subcontractors',
            type: 'multiselect',
            multiselectOptions: {
                placeholder: 'Type company name to search',
                label: 'name',
                multiple: true,
            },
            async doSearch (query) {
                return Api.getItems(this, 'company', { name: { field: 'name', value: query } })
            }
        },
        //Label only fields for view table
        id: {
            label: 'ID',
        },
        status: {
            label: 'Status',
        },
        rentals: {
            label: 'Rentals'
        },
        agency: {
            label: 'Agency'
        },
        projectManager: {
            label: 'Project Manager'
        },
        primeCompanyProjects: {
            label: 'Prime Contractors'
        },
        subCompanyProjects: {
            label: 'Subcontractors'
        },
        closed: {
            label: 'Closed Date'
        }
    }
}
